import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { MoneyDisplay } from '@/components/MoneyDisplay'
import type { MatchedItem, Category, Transaction } from '@/types'
import { ArrowRight, Link2, X, FileText, ImageIcon, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MatchedItemCardProps {
  match: MatchedItem
  categories: Category[]
}

function isExactMatch(item: MatchedItem['item'], txn: Transaction): boolean {
  if (!item.amount) return false
  return txn.amount.cents === -(item.amount.cents)
}

export function MatchedItemCard({ match, categories }: MatchedItemCardProps) {
  const { item, candidates } = match
  const [selectedTxnId, setSelectedTxnId] = useState(match.transaction.id)
  const [receiptOpen, setReceiptOpen] = useState(false)
  const selectedTxn = candidates.find((t) => t.id === selectedTxnId) || match.transaction

  const category = item.category_id
    ? categories.find((c) => c.id === item.category_id)
    : null
  const txnCategory = selectedTxn.category_id
    ? categories.find((c) => c.id === selectedTxn.category_id)
    : null

  function handleLink() {
    router.post(`/reimbursement_items/${item.id}/link`, { transaction_id: selectedTxnId }, { preserveScroll: true })
  }

  function handleDismiss() {
    router.post(`/reimbursement_items/${item.id}/unlink`, {}, { preserveScroll: true })
  }

  const hasMultipleCandidates = candidates.length > 1

  function TransactionDisplay({ className }: { className?: string }) {
    return (
      <div className={cn('flex flex-1 items-center gap-3 min-w-0 rounded-md bg-muted/30 px-3 py-2', className)}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] shrink-0">SBI</Badge>
            <span className="text-[13px] font-medium text-foreground truncate">
              {selectedTxn.description || selectedTxn.memo || 'No description'}
            </span>
            {isExactMatch(item, selectedTxn) && (
              <Badge variant="outline" className="text-[10px] shrink-0 bg-emerald-500/15 border-emerald-500/20 text-emerald-700 dark:text-emerald-400">Exact</Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <span>{selectedTxn.date}</span>
            {txnCategory && <span>{txnCategory.name}</span>}
            {hasMultipleCandidates && (
              <span className="text-muted-foreground/60">{candidates.length} matches</span>
            )}
            {!hasMultipleCandidates && selectedTxn.memo && selectedTxn.description && (
              <span className="truncate">{selectedTxn.memo}</span>
            )}
          </div>
        </div>
        <div className="shrink-0">
          <MoneyDisplay money={selectedTxn.amount} size="sm" />
        </div>
        {hasMultipleCandidates && (
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
        )}
      </div>
    )
  }

  return (
    <>
      <div className="rounded-lg border bg-card">
        <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-4">
        {/* Receipt side */}
        <div className="flex flex-1 items-center gap-3 min-w-0">
          {item.receipt ? (
            <button
              type="button"
              className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md border bg-muted/50 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setReceiptOpen(true)}
              aria-label={`View receipt ${item.receipt.filename}`}
              title="View receipt"
            >
              {item.receipt.content_type?.startsWith('image/') && item.receipt.url ? (
                <img src={item.receipt.url} alt="" className="h-full w-full object-cover" />
              ) : (
                <FileText className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/50">
              <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-foreground truncate">
              {item.memo || 'No description'}
            </p>
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
              {item.date && <span>{item.date}</span>}
              {category && <span>{category.name}</span>}
            </div>
          </div>
          <div className="ml-auto shrink-0">
            {item.amount && <MoneyDisplay money={item.amount} size="sm" />}
          </div>
        </div>

        {/* Arrow */}
        <div className="hidden md:flex shrink-0 items-center">
          <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
        </div>

        {/* Transaction side */}
        {hasMultipleCandidates ? (
          <div className="flex flex-1 min-w-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex flex-1 min-w-0 cursor-pointer rounded-md ring-1 ring-transparent hover:ring-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <TransactionDisplay />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[420px]">
                <DropdownMenuLabel className="text-[11px] text-muted-foreground font-normal">
                  Select a transaction to link
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={String(selectedTxnId)} onValueChange={(v) => setSelectedTxnId(Number(v))}>
                  {candidates.map((txn) => {
                    const candidateCategory = txn.category_id
                      ? categories.find((c) => c.id === txn.category_id)
                      : null
                    const exact = isExactMatch(item, txn)
                    return (
                      <DropdownMenuRadioItem key={txn.id} value={String(txn.id)} className="py-2.5 px-3 cursor-pointer">
                        <div className="flex items-center gap-3 w-full">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[13px] font-medium truncate">
                                {txn.description || txn.memo || 'No description'}
                              </span>
                              {exact && (
                                <Badge variant="outline" className="text-[10px] shrink-0 bg-emerald-500/15 border-emerald-500/20 text-emerald-700 dark:text-emerald-400">Exact</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                              <span>{txn.date}</span>
                              {candidateCategory && <span>{candidateCategory.name}</span>}
                            </div>
                          </div>
                          <span className="text-[13px] tabular-nums font-medium shrink-0">
                            {txn.amount.formatted}
                          </span>
                        </div>
                      </DropdownMenuRadioItem>
                    )
                  })}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <TransactionDisplay />
        )}

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-1.5">
          <Button size="sm" className="h-8" onClick={handleLink}>
            <Link2 className="h-3.5 w-3.5 mr-1.5" />
            Link
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground"
            onClick={handleDismiss}
            title="Dismiss match"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        </div>
      </div>

      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          {item.receipt && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[15px]">{item.receipt.filename}</DialogTitle>
              </DialogHeader>
              <div className="flex min-h-[360px] items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
                {item.receipt.content_type === 'application/pdf' ? (
                  <iframe
                    src={item.receipt.url}
                    title={item.receipt.filename}
                    className="h-[75vh] w-full"
                  />
                ) : (
                  <img
                    src={item.receipt.url}
                    alt={item.receipt.filename}
                    className="max-h-[75vh] w-full object-contain"
                  />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
